








import { Component, Prop, Vue } from 'vue-property-decorator'
import { ChartData, ChartOptions } from 'chart.js'

export interface AchievedDoughnutChartData {
    achieved: number,
    target: number,
}

@Component
export default class AchievedDoughnutChart extends Vue {
    @Prop({ required: true, default: () => [] }) readonly data!: AchievedDoughnutChartData
    @Prop() readonly formatter!: (value: number, precision?: number) => string

    get chartData(): ChartData {
        const labels = []
        const data = []
        const backgroundColor = []

        if (this.achieved > 0) {
            labels.push(this.$t('reports.salesReport.achieved').toString())
            data.push(this.achieved)
            backgroundColor.push('rgb(0, 163, 137)')
        }

        if (this.remainingTarget > 0) {
            labels.push(this.$t('reports.salesReport.remainingTarget').toString())
            data.push(this.remainingTarget)
            backgroundColor.push('rgb(232, 234, 238)')
        }

        if (this.isEmpty) {
            data.push(1)
            backgroundColor.push('rgb(232, 234, 238)')
        }

        return {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor,
                },
            ],
        }
    }

    get chartOptions(): ChartOptions<'doughnut'> {
        return {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2,

            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: !this.isEmpty,
                    callbacks: {
                        label: tooltipItem => {
                            return tooltipItem.label + ': ' + this.formatValue(tooltipItem.raw as number)
                        },
                    },
                },
            },
            circumference: 180,
            rotation: -90,
            cutout: '66%',
        }
    }

    get chartPlugins(): object[] {
        return [
            this.chartDrawPercentagePlugin,
        ]
    }

    get chartDrawPercentagePlugin(): object {
        return {
            afterDraw: chart => {
                // Prepare chart
                const ctx = chart.ctx
                ctx.restore()

                ctx.fillStyle = '#1e2a46'
                ctx.font = '18px \'SofiaPro-Regular\', \'Avenir\', Helvetica, Arial, sans-serif'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'bottom'

                const positionX = chart.width / 2
                const positionY = chart.height

                // Draw achieved
                ctx.fillText(
                    this.formatValue(this.achieved),
                    positionX,
                    positionY - 24,
                )

                // Calculate percentage achieved
                if (!this.target) {
                    return
                }

                const percentage = this.achieved / this.target

                // Draw percentage achieved
                ctx.fillStyle = '#a0aec0'
                ctx.fillText(
                    percentage.toLocaleString('nl', { style: 'percent' }),
                    positionX,
                    positionY,
                )

                // Draw
                ctx.save()
            },
        }
    }

    get achieved(): number {
        return this.data.achieved || 0
    }

    get target(): number {
        return this.data.target || 0
    }

    get remainingTarget(): number {
        return Math.max(0, this.data.target - this.data.achieved)
    }

    get isEmpty(): boolean {
        return this.achieved === 0 && this.remainingTarget === 0
    }

    formatValue(value: number, precision?: number): string {
        value = Number(value)

        return this.formatter ? this.formatter(value, precision) : value.toString()
    }
}
